
import { supabase } from "@/integrations/supabase/client";
import { Media } from "../../../../media/types";

/**
 * Deletes media from both database and storage
 */
export const deleteMediaFromDatabase = async (mediaId: string) => {

  try {
    // First, get the media record to find the storage path
    const { data: mediaRecord, error: fetchError } = await supabase
      .from('media')
      .select('id, url')
      .eq('id', mediaId)
      .maybeSingle();

    if (fetchError) {
      console.error('[deleteMediaFromDatabase] Error fetching media record:', fetchError);
      throw fetchError;
    }

    if (!mediaRecord) {
      // Best-effort database delete in case it exists
      await supabase.from('media').delete().eq('id', mediaId);
      return true;
    }

    // Extract storage path from URL if it's a Supabase storage URL
    let storagePath = null;
    if (mediaRecord.url.includes('memorials/')) {
      // Extract path from public URL
      const urlParts = mediaRecord.url.split('memorials/');
      if (urlParts.length > 1) {
        storagePath = urlParts[1].split('?')[0]; // Remove query params if any
      }
    }

    // Delete from storage if we have a path
    if (storagePath) {
      const { error: storageError } = await supabase.storage
        .from('memorials')
        .remove([storagePath]);

      if (storageError) {
        console.error('[deleteMediaFromDatabase] Storage deletion error:', storageError);
        // Don't throw here - continue with database deletion even if storage fails
      }
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('media')
      .delete()
      .eq('id', mediaId);

    if (dbError) {
      console.error('[deleteMediaFromDatabase] Database deletion error:', dbError);
      throw dbError;
    }

    return true;
  } catch (error) {
    console.error('[deleteMediaFromDatabase] Error deleting media:', error);
    throw error;
  }
};

/**
 * Handles removing media from a story's media array and deleting from database
 */
export const handleStoryMediaRemoval = async (
  mediaArray: Media[],
  indexToRemove: number
) => {

  if (indexToRemove < 0 || indexToRemove >= mediaArray.length) {
    console.error('[handleStoryMediaRemoval] Invalid index:', indexToRemove);
    return { success: false, updatedMedia: mediaArray };
  }

  const mediaToDelete = mediaArray[indexToRemove];

  if (!mediaToDelete || !mediaToDelete.id) {
    const updatedMedia = [...mediaArray];
    updatedMedia.splice(indexToRemove, 1);
    return { success: true, updatedMedia };
  }

  try {
    // Delete from database and storage
    await deleteMediaFromDatabase(mediaToDelete.id);

    // Remove from array
    const updatedMedia = [...mediaArray];
    updatedMedia.splice(indexToRemove, 1);

    return { success: true, updatedMedia };
  } catch (error) {
    console.error('[handleStoryMediaRemoval] Failed to delete media:', error);
    return { success: false, updatedMedia: mediaArray, error };
  }
};
